import * as React from 'react';
import { Button, Input } from 'antd';
import { ArrowsAltOutlined } from '@ant-design/icons';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { PromptTemplateBlock, PromptTemplateStructureEnum } from '../../types';
import { DraggableBlock } from './DraggableBlock';
import PreviewContent from './PreviewContent';
import { usePreviewWindow } from '../../../shared/hooks/usePreviewWindow';
import {
    addBlock,
    updateBlock,
    removeBlock,
    reorderBlocks,
    updateCollapsedKeys,
    expandAllBlocks,
    areAllExpanded,
    calculateWordCount,
    concatenateBlockBodies
} from '../../misc';
import WindowPortal from '../../../shared/components/WindowPortal';

interface BlocksManagerProps {
    blocks: PromptTemplateBlock[];
    collapsedKeys: string[];
    onBlocksChange: (blocks: PromptTemplateBlock[]) => void;
    onCollapsedKeysChange: (keys: string[]) => void;
    templateStructure: PromptTemplateStructureEnum;
}

const BlocksManager: React.FC<BlocksManagerProps> = ({
    blocks,
    collapsedKeys,
    onBlocksChange,
    onCollapsedKeysChange,
    templateStructure
}) => {
    const [dragingIndex, setDragingIndex] = React.useState<number>(-1);
    const { externalWindow, setExternalWindow } = usePreviewWindow();
    const [showPreviewWindow, setShowPreviewWindow] = React.useState<boolean>(false);

    const handleAddBlock = () => {
        const result = addBlock(blocks, collapsedKeys);
        onBlocksChange(result.blocks);
        onCollapsedKeysChange(result.collapsedKeys);
    };

    const handleBlockChange = (index: number, field: keyof PromptTemplateBlock, value: string) => {
        const newBlocks = updateBlock(blocks, index, field, value);
        onBlocksChange(newBlocks);
    };

    const handleRemoveBlock = (index: number) => {
        const newBlocks = removeBlock(blocks, index);
        onBlocksChange(newBlocks);
    };

    const moveBlock = (dragIndex: number, hoverIndex: number) => {
        const newBlocks = reorderBlocks(blocks, dragIndex, hoverIndex);
        onBlocksChange(newBlocks);
    };

    const handleCollapseChange = (key: string | string[]) => {
        const newKeys = updateCollapsedKeys(key);
        onCollapsedKeysChange(newKeys);
    };

    const handleExpandAll = () => {
        const newKeys = expandAllBlocks(blocks);
        onCollapsedKeysChange(newKeys);
    };

    const handleCollapseAll = () => {
        const newKeys: string[] = [];
        onCollapsedKeysChange(newKeys);
    };

    const handleOpenPreviewInNewWindow = React.useCallback(() => {
        setShowPreviewWindow(true);
    }, []);

    const handleClosePreview = React.useCallback(() => {
        setShowPreviewWindow(false);
        setExternalWindow(undefined);
    }, [setExternalWindow]);

    const areAllBlocksExpanded = areAllExpanded(blocks, collapsedKeys);

    return (
        <>
            <div className="block-section-container">
                <div className="block-header">
                    <label className="block-label">Body Blocks</label>
                    {blocks.length > 0 && (
                        <Button
                            type="link"
                            size="small"
                            onClick={areAllBlocksExpanded ? handleCollapseAll : handleExpandAll}
                            className="expand-collapse-button"
                        >
                            {areAllBlocksExpanded ? 'Collapse All' : 'Expand All'}
                        </Button>
                    )}
                </div>
                <DndProvider backend={HTML5Backend}>
                    <div className="blocks-container">
                        {blocks.map((block, index) => (
                            <DraggableBlock
                                key={`block-${index}`}
                                block={block}
                                index={index}
                                canRemove={blocks.length > 1}
                                onBlockChange={handleBlockChange}
                                onRemoveBlock={handleRemoveBlock}
                                moveBlock={moveBlock}
                                activeKey={collapsedKeys}
                                onCollapseChange={handleCollapseChange}
                                dragingIndex={dragingIndex}
                                onDragStart={setDragingIndex}
                                templateStructure={templateStructure}
                            />
                        ))}
                    </div>
                </DndProvider>
                <Button onClick={handleAddBlock} className="add-block-button">
                    Add Block
                </Button>
            </div>

            <div className="preview-section-container">
                <div className="preview-header">
                    <label className="preview-label">Preview</label>
                    <div className="preview-header-controls">
                        <span className="word-count">{calculateWordCount(blocks)} tokens</span>
                        <Button
                            type="text"
                            size="small"
                            icon={<ArrowsAltOutlined />}
                            onClick={handleOpenPreviewInNewWindow}
                            title="Open preview in new window"
                            className="preview-expand-button"
                        />
                    </div>
                </div>
                <Input.TextArea
                    value={concatenateBlockBodies(blocks)}
                    readOnly
                    autoSize={{ minRows: 4, maxRows: 12 }}
                    placeholder="Preview of all blocks concatenated together will appear here..."
                    className="preview-textarea"
                />
            </div>

            {showPreviewWindow && (
                <WindowPortal
                    externalWindow={externalWindow}
                    setExternalWindow={setExternalWindow}
                    onWindowClose={handleClosePreview}
                    title="Prompt Template Preview"
                >
                    <PreviewContent blocks={blocks} />
                </WindowPortal>
            )}
        </>
    );
};

export default BlocksManager;
